<template>
  <v-row>
    <v-breadcrumbs :items="['reports']" divider="/"></v-breadcrumbs>
  </v-row>
  <v-row>
    <v-col cols="12" md="12">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">portfolio snapshot</v-card-title>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text>
          <v-container>
            <v-row align="center">
              <v-col cols="12" md="3">
                <v-select
                  v-model="selectedBrokerAccount"
                  :items="brokerAccounts"
                  item-title="label"
                  item-value="id"
                  label="broker account"
                  density="compact"
                  variant="outlined"
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="selectedFY"
                  :items="financialYears"
                  label="financial year"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  @update:modelValue="onFYSelect"
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="snapshotDate"
                  type="date"
                  label="snapshot date"
                  density="compact"
                  variant="outlined"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="auto">
                <v-btn @click="getSnapshot" :loading="loading" :disabled="!selectedBrokerAccount || !snapshotDate">go</v-btn>
                <v-btn class="ml-2" variant="outlined" @click="exportPDF" :disabled="!snapshot" prepend-icon="mdi-file-pdf-box">pdf</v-btn>
                <v-btn class="ml-2" variant="outlined" @click="exportCSV" :disabled="!snapshot" prepend-icon="mdi-file-delimited">csv</v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <v-row v-if="snapshot">
    <v-col cols="12" md="12">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">positions</v-card-title>
          <template v-slot:append>
            <v-btn v-if="snapshot.has_foreign_currency" icon="mdi-currency-usd" :variant="isBaseCurrency ? 'tonal' : 'text'" @click="isBaseCurrency = !isBaseCurrency"></v-btn>
          </template>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text>
          <v-container class="table-scroll">
            <div v-if="snapshot.positions.length === 0" class="text-left text-grey">no open positions as of this date</div>
            <table v-else>
              <tr>
                <th>ticker</th>
                <th>currency</th>
                <th>units</th>
                <th>unit cost</th>
                <th>cost base</th>
              </tr>
              <tr v-for="position in snapshot.positions" :key="position.ticker">
                <td>{{ position.ticker }}</td>
                <Transition mode="out-in" name="fade-in">
                  <td :key="isBaseCurrency">{{ isBaseCurrency ? 'AUD' : position.currency }}</td>
                </Transition>
                <td>{{ position.units.toLocaleString() }}</td>
                <Transition mode="out-in" name="fade-in">
                  <td :key="isBaseCurrency">{{ (isBaseCurrency ? position.unit_cost_base_currency : position.unit_cost).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</td>
                </Transition>
                <Transition mode="out-in" name="fade-in">
                  <td :key="isBaseCurrency">{{ (isBaseCurrency ? position.cost_base_base_currency : position.cost_base).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</td>
                </Transition>
              </tr>
              <tr>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <Transition mode="out-in" name="fade-in">
                  <td :key="isBaseCurrency" class="font-weight-bold">{{ (isBaseCurrency ? snapshot.total_cost_base_base_currency : snapshot.total_cost_base).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</td>
                </Transition>
              </tr>
            </table>
          </v-container>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <v-row v-if="snapshot && snapshot.txns.length > 0">
    <v-col cols="12" md="12">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">txns</v-card-title>
          <template v-slot:append>
            <v-btn v-if="snapshot.has_foreign_currency" icon="mdi-currency-usd" :variant="isBaseCurrency ? 'tonal' : 'text'" @click="isBaseCurrency = !isBaseCurrency"></v-btn>
          </template>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text>
          <v-container class="table-scroll">
            <table>
              <tr>
                <th>txn date</th>
                <th>ticker</th>
                <th>currency</th>
                <th>type</th>
                <th>units</th>
                <th>price</th>
                <th>gross amount</th>
                <th>brokerage</th>
                <th>net amount</th>
                <th v-if="snapshot.has_foreign_currency">fx rate</th>
              </tr>
              <tr v-for="txn in snapshot.txns.slice().reverse()" :key="txn.id">
                <td>{{ showDateFormat(txn.txn_date) }}</td>
                <td>{{ txn.ticker }}</td>
                <Transition mode="out-in" name="fade-in">
                  <td :key="isBaseCurrency">{{ isBaseCurrency ? txn.base_currency.currency : txn.currency }}</td>
                </Transition>
                <td>{{ txn.txn_type }}</td>
                <td>{{ txn.qty.toLocaleString() }}</td>
                <Transition mode="out-in" name="fade-in">
                  <td :key="isBaseCurrency">{{ (isBaseCurrency ? txn.base_currency.price : txn.price).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</td>
                </Transition>
                <Transition mode="out-in" name="fade-in">
                  <td :key="isBaseCurrency">{{ (isBaseCurrency ? txn.base_currency.gross_amount : txn.gross_amount).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</td>
                </Transition>
                <Transition mode="out-in" name="fade-in">
                  <td :key="isBaseCurrency">{{ (isBaseCurrency ? txn.base_currency.brokerage : txn.brokerage).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</td>
                </Transition>
                <Transition mode="out-in" name="fade-in">
                  <td :key="isBaseCurrency">{{ (isBaseCurrency ? txn.base_currency.net_amount : txn.net_amount).toLocaleString("en", {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</td>
                </Transition>
                <td v-if="snapshot.has_foreign_currency">{{ txn.base_currency_spot_rate ?? '' }}</td>
              </tr>
            </table>
          </v-container>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import axios from 'axios';
import { mapState, mapMutations } from 'vuex';
import moment from 'moment';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import SpaceGroteskRegular from '@/assets/SpaceGroteskFont.js';

export default {
  data() {
    return {
      brokerAccounts: [],
      selectedBrokerAccount: null,
      selectedFY: null,
      snapshotDate: '',
      snapshot: null,
      loading: false,
      isBaseCurrency: false
    };
  },
  async created() {
    this.getBrokerAccounts();
  },
  computed: {
    ...mapState({
      token: "token"
    }),
    financialYears() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      const latestEndYear = currentMonth >= 6 ? currentYear + 1 : currentYear;
      const years = [];
      for (let endYear = latestEndYear; endYear >= latestEndYear - 5; endYear--) {
        years.push('FY' + (endYear - 1).toString().slice(2) + '/' + endYear.toString().slice(2));
      }
      return years;
    }
  },
  methods: {
    ...mapMutations(["showSnackbar"]),
    onFYSelect(val) {
      if (!val) return;
      const parts = val.replace('FY', '').split('/');
      const endYear = 2000 + parseInt(parts[1]);
      this.snapshotDate = endYear + '-06-30';
      if (this.selectedBrokerAccount) this.getSnapshot();
    },
    async getBrokerAccounts() {
      try {
        const response = await axios.get("/securities/brokeraccounts/", {
          headers: { Authorization: `Bearer ${this.token}` }
        });
        this.brokerAccounts = response.data.map(a => ({
          id: a.id,
          label: a.institution + ' - ' + a.account_name
        }));
      } catch (error) {
        this.showSnackbar({ message: 'Failed to load broker accounts' });
      }
    },
    async getSnapshot() {
      this.loading = true;
      this.snapshot = null;
      this.isBaseCurrency = false;
      try {
        const response = await axios.get("/reports/portfoliosnapshot/" + this.selectedBrokerAccount + "?date=" + this.snapshotDate, {
          headers: { Authorization: `Bearer ${this.token}` }
        });
        this.snapshot = response.data;
      } catch (error) {
        this.showSnackbar({ message: 'Failed to load portfolio snapshot' });
      }
      this.loading = false;
    },
    showDateFormat(rawDate) {
      return moment(rawDate).format('DD/MM/YYYY');
    },
    selectedAccountLabel() {
      const acct = this.brokerAccounts.find(a => a.id === this.selectedBrokerAccount);
      return acct ? acct.label : '';
    },
    fmtNum(v) {
      return v.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    positionRow(p, fmt = false) {
      const bc = this.isBaseCurrency;
      const f = fmt ? this.fmtNum : v => v.toFixed(2);
      return [
        p.ticker,
        bc ? 'AUD' : p.currency,
        fmt ? p.units.toLocaleString() : p.units,
        f(bc ? p.unit_cost_base_currency : p.unit_cost),
        f(bc ? p.cost_base_base_currency : p.cost_base)
      ];
    },
    txnRow(t, fmt = false) {
      const bc = this.isBaseCurrency;
      const f = fmt ? this.fmtNum : v => v.toFixed(2);
      const row = [
        this.showDateFormat(t.txn_date),
        t.ticker,
        bc ? t.base_currency.currency : t.currency,
        t.txn_type,
        fmt ? t.qty.toLocaleString() : t.qty,
        f(bc ? t.base_currency.price : t.price),
        f(bc ? t.base_currency.gross_amount : t.gross_amount),
        f(bc ? t.base_currency.brokerage : t.brokerage),
        f(bc ? t.base_currency.net_amount : t.net_amount)
      ];
      if (this.snapshot.has_foreign_currency) {
        row.push(t.base_currency_spot_rate ?? '');
      }
      return row;
    },
    async exportPDF() {
      const doc = new jsPDF();

      // register Space Grotesk font
      doc.addFileToVFS('SpaceGrotesk-Regular.ttf', SpaceGroteskRegular);
      doc.addFont('SpaceGrotesk-Regular.ttf', 'SpaceGrotesk', 'normal');
      doc.addFont('SpaceGrotesk-Regular.ttf', 'SpaceGrotesk', 'bold');
      doc.setFont('SpaceGrotesk');

      const label = this.selectedAccountLabel();
      const total = this.isBaseCurrency ? this.snapshot.total_cost_base_base_currency : this.snapshot.total_cost_base;

      // load logo
      try {
        const response = await fetch('/w_icon_medium.png');
        const blob = await response.blob();
        const dataUrl = await new Promise(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        doc.addImage(dataUrl, 'PNG', 14, 10, 12, 12);
      } catch (e) {
        // continue without logo if it fails to load
      }

      // branding
      doc.setFontSize(18);
      doc.text('wellfi', 29, 19);

      // report header
      doc.setFontSize(12);
      doc.text('Portfolio Snapshot', 14, 30);
      doc.setFontSize(10);
      doc.text(label + '  |  ' + moment(this.snapshotDate).format('DD-MM-YYYY'), 14, 36);

      // positions table
      doc.setFontSize(11);
      doc.text('Positions', 14, 44);

      const posHeaders = ['Ticker', 'Currency', 'Units', 'Unit Cost', 'Cost Base'];
      const posRows = this.snapshot.positions.map(p => this.positionRow(p, true));
      posRows.push(['', '', '', '', this.fmtNum(total)]);

      autoTable(doc, {
        startY: 48,
        head: [posHeaders],
        body: posRows,
        styles: { fontSize: 9, font: 'SpaceGrotesk' },
        headStyles: { fillColor: [0, 0, 0], font: 'SpaceGrotesk' },
        didParseCell: (data) => {
          if (data.row.index === posRows.length - 1 && data.section === 'body') {
            data.cell.styles.fontStyle = 'bold';
          }
        }
      });

      // txns table
      if (this.snapshot.txns.length > 0) {
        const txnY = doc.lastAutoTable.finalY + 14;
        doc.setFontSize(11);
        doc.text('Txns', 14, txnY);

        const txnHeaders = ['Date', 'Ticker', 'Currency', 'Type', 'Units', 'Price', 'Gross', 'Brokerage', 'Net'];
        if (this.snapshot.has_foreign_currency) txnHeaders.push('FX Rate');
        const txnRows = this.snapshot.txns.slice().reverse().map(t => this.txnRow(t, true));

        autoTable(doc, {
          startY: txnY + 4,
          head: [txnHeaders],
          body: txnRows,
          styles: { fontSize: 8, font: 'SpaceGrotesk' },
          headStyles: { fillColor: [0, 0, 0], font: 'SpaceGrotesk' }
        });
      }

      doc.save('portfolio_snapshot_' + this.snapshotDate + '.pdf');
    },
    exportCSV() {
      const label = this.selectedAccountLabel();
      const total = this.isBaseCurrency ? this.snapshot.total_cost_base_base_currency : this.snapshot.total_cost_base;
      const lines = [];

      lines.push('Portfolio Snapshot - ' + label + ' - ' + moment(this.snapshotDate).format('DD-MM-YYYY'));
      lines.push('');
      lines.push('Positions');
      lines.push(['Ticker', 'Currency', 'Units', 'Unit Cost', 'Cost Base'].join(','));
      this.snapshot.positions.forEach(p => {
        lines.push(this.positionRow(p).join(','));
      });
      lines.push(['', '', '', '', total.toFixed(2)].join(','));

      if (this.snapshot.txns.length > 0) {
        lines.push('');
        lines.push('Transactions');
        const csvTxnHeaders = ['Date', 'Ticker', 'Currency', 'Type', 'Units', 'Price', 'Gross Amount', 'Brokerage', 'Net Amount'];
        if (this.snapshot.has_foreign_currency) csvTxnHeaders.push('FX Rate');
        lines.push(csvTxnHeaders.join(','));
        this.snapshot.txns.slice().reverse().forEach(t => {
          lines.push(this.txnRow(t).join(','));
        });
      }

      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'portfolio_snapshot_' + this.snapshotDate + '.csv';
      a.click();
      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>
table{
  width: 100%;
  border-collapse: collapse;
}

.fade-in-enter-active {
  transition: opacity 300ms cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.fade-in-leave-active {
  transition: opacity 225ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.fade-in-enter,
.fade-in-leave-to {
  opacity: 0;
}
</style>
